import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SuiSpinner } from '@splashthat/sui';
import Logger from 'observability/Logger';
import { SpinnerWrapper, StyledIframe } from './Iframe.styles';

const LOG = new Logger('IFrame');
const BASE_CLASS_NAME = 'IFrame';

/*
 * Factory function that ensures the MessageChannel API is available in the current environment. All browsers we support
 * do have support for the API, but jsDom doesn't and this causes tests to fail when running this code via Jest.
 * Therefore the IFrame component's implementation can't take the availability of this API for granted.
 */
function buildMessageChannel() {
    let channel;
    if (window.MessageChannel) {
        channel = new window.MessageChannel();
    }
    return channel;
}

export default class IFrame extends Component {
    static propTypes = {
        url: PropTypes.string.isRequired,
        onIframeLoaded: PropTypes.func,
        onComponentMount: PropTypes.func,
        className: PropTypes.string,
        name: PropTypes.string,
        heightAuto: PropTypes.bool,
        canEditChild: PropTypes.bool,
        isLoading: PropTypes.bool,
        onMessageFromIframe: PropTypes.func,
        allowClipboardWrite: PropTypes.bool,
    };

    static defaultProps = {
        onIframeLoaded: () => {},
        onComponentMount: () => {},
        className: '',
        name: null,
        heightAuto: false,
        canEditChild: true,
        isLoading: false,
        onMessageFromIframe: () => {},
        allowClipboardWrite: false,
    };

    constructor(props) {
        super(props);
        this.onMessageFromIframe = this.onMessageFromIframe.bind(this);
    }

    onMessageFromIframe(event) {
        this.props.onMessageFromIframe(event);
    }

    initializeChannel(retries = 0) {
        const MAX_RETRIES = 3;
        const RETRY_INTERVAL = 1000;
        if (retries > MAX_RETRIES) {
            LOG.warn(`[IFrame] Could not initialize message channel after ${MAX_RETRIES} attempts.`);
            return;
        }

        if (!this.channel) {
            this.channel = buildMessageChannel();
        }

        // Listen for messages on port1
        this.channel.port1.onmessage = this.onMessageFromIframe;
        // Send a message to the iframe to initialize the channel
        try {
            this.iframe.contentWindow.postMessage({ message: 'init-message-channel' }, this.props.url, [
                this.channel.port2,
            ]);
        } catch (error) {
            // TODO Find out why this.channel.port2 can't be serialized after a reload of the iframe
            //  causing https://splashthat.atlassian.net/browse/SWP-101236.
            LOG.warn(error);

            // eslint-disable-next-line no-unused-expressions
            this.channel?.port1?.close?.();
            this.channel = null;
            setTimeout(() => this.initializeChannel(retries + 1), RETRY_INTERVAL);
        }
    }

    componentDidMount() {
        this.props.onComponentMount();

        this.channel = buildMessageChannel();

        if (this.iframe) {
            this.iframe.addEventListener('load', (e) => {
                this.initializeChannel();
                this.handleIframeLoaded(e);
            });
        }
    }

    componentWillUnmount() {
        // eslint-disable-next-line no-unused-expressions
        this.channel?.port1.close();
    }

    handleIframeLoaded = () => {
        const iframeWindow = this.iframe && this.iframe.contentDocument && this.iframe.contentDocument.defaultView;
        // TODO Eventually, remove iframeWindow from the returned values, every operation should be done with
        //  sendMessageToIframe
        this.props.onIframeLoaded({
            iframeWindow,
            sendMessageToIframe: (message) => this.channel?.port1.postMessage(message),
        });
    };

    setIframeRef = (iframeElement) => {
        if (iframeElement) {
            this.iframe = iframeElement;
        }
    };

    render() {
        const heightProps = this.props.heightAuto ? {} : { height: '100%' };

        return (
            <>
                {this.props.isLoading && (
                    <SpinnerWrapper>
                        <SuiSpinner size="large" />
                    </SpinnerWrapper>
                )}
                <StyledIframe
                    $isLoading={this.props.isLoading}
                    className={`${BASE_CLASS_NAME} ${this.props.className}`}
                    ref={this.setIframeRef}
                    src={this.props.url}
                    allow={this.props.allowClipboardWrite ? 'clipboard-write' : null}
                    {...heightProps}
                    width="100%"
                    name={this.props.name}
                    $canEditChild={this.props.canEditChild}
                />
            </>
        );
    }
}
